/**
 * End-Game Inline Result Card - the post-dismiss flat-mounted score card.
 *
 * Per `Vault/Design/Design Brief.md` "Post-dismiss inline result card
 * (PM 2026-06-10 v3)" and the memory rule
 * `feedback_end_game_inline_restore.md` (Pattern 2):
 *
 *   - When the user dismisses the end-game overlay (close X / ESC /
 *     backdrop tap / Back to puzzles), the consumer game hides its
 *     keypad / keyboard / typing input and mounts THIS card in the
 *     freed space below the board.
 *   - The card mirrors the modal's stat block: hero / time / sub-line
 *     / Rank: TBD placeholder. Same `--success` / `--failure` colour
 *     bar at the top, same rounded modal radius. Flat-mounted in page
 *     flow, NOT centred over a backdrop.
 *   - The card itself is the whole-tap target. Tapping anywhere on it
 *     calls `props.onTap`, which the consumer uses to re-mount the
 *     full end-game overlay with the same stats + CTAs.
 *   - 44pt minimum tap height + `touch-action: manipulation`
 *     (matches Design Brief mobile tap targets).
 *   - Persistence (localStorage `gives.endgame.<gameId>.<utcDate>`)
 *     is the consumer's responsibility - this component is pure UI;
 *     the helpers `endgameStorageKey` / `loadEndgameSnapshot` /
 *     `saveEndgameSnapshot` / `clearEndgameSnapshot` are exposed
 *     alongside for that wiring.
 *
 * Reuse: every game registers this card via the same `EndGameStats`
 * shape it already passes to `mountEndGameOverlay`. The Outcome banner
 * text defaults to "Solved!" / "Not solved today." matching the modal.
 */

import {
  applyParClassAttr,
  type EndGameOutcome,
  type EndGameStats,
} from './end-game-overlay';

/**
 * Inline-card outcomes mirror the spec's wording (`won` / `lost` /
 * `timed_out`) rather than the modal's `win` / `loss` so consumers can
 * pass the raw `/finish` status straight through. We map down to the
 * modal's two-state outcome for the colour-bar paint.
 */
export type InlineCardOutcome = 'won' | 'lost' | 'timed_out';

export interface EndGameInlineCardProps {
  /** Same shape as the modal's stats. */
  stats: EndGameStats;
  /** `won` / `lost` / `timed_out`. `won` paints `--success`, the others paint `--failure`. */
  outcome: InlineCardOutcome;
  /** Whole-card tap callback. The consumer re-opens the modal here. */
  onTap: () => void;
  /** Optional override for the colour-bar caption. Defaults match the modal. */
  bannerText?: string;
  /**
   * Optional override for the bottom "Tap to view ..." hint chip.
   *
   * PM 2026-06-11: when the board behind the card carries the FULL SOLUTION
   * (spatial-config games like MathDoku / Nonogram / Minesweeper / Connections
   * on loss), the modal no longer adds new information, so the hint should
   * read "Tap to view stats" rather than "Tap to view result". Text-answer
   * games (Worldle, LongWord, Equate ...) still surface the answer through
   * the modal, so they keep the default.
   *
   * PM 2026-06-11 (Connect4 case): pass an empty string to HIDE the hint
   * chip AND disable the tap-to-reopen affordance. Used when re-opening
   * the modal adds nothing the inline card does not already show (Connect4
   * has no solution to reveal - the outcome depends on play). The card
   * becomes a non-interactive flat result card.
   */
  hintText?: string;
}

export interface EndGameInlineCardHandle {
  /** Programmatically swap props (e.g. when stats refresh). */
  update: (props: EndGameInlineCardProps) => void;
  /** Tear down + detach the DOM. */
  destroy: () => void;
}

function defaultBannerText(outcome: InlineCardOutcome): string {
  if (outcome === 'won') return 'Solved!';
  if (outcome === 'timed_out') return 'Time up.';
  return 'Not solved today.';
}

function statBarOutcome(outcome: InlineCardOutcome): EndGameOutcome {
  return outcome === 'won' ? 'win' : 'loss';
}

interface InternalRefs {
  card: HTMLButtonElement;
  banner: HTMLDivElement;
  bannerText: HTMLSpanElement;
  body: HTMLDivElement;
  statHero: HTMLParagraphElement;
  statTime: HTMLParagraphElement;
  statPrimary: HTMLParagraphElement;
  statLines: HTMLDivElement;
  pointsBlock: HTMLDivElement;
  pointsValue: HTMLParagraphElement;
  rankBlock: HTMLDivElement;
  rankLabel: HTMLParagraphElement;
  rankCaption: HTMLParagraphElement;
  hint: HTMLParagraphElement;
}

function buildSkeleton(): InternalRefs {
  // The card is a <button> so the WHOLE thing is one tap target with
  // built-in keyboard / focus / a11y semantics.
  //
  // PM 2026-06-11 (tap-to-view audit): when the consumer passes an empty
  // `hintText`, we strip the interactive affordances (`data-interactive`)
  // and the CSS hides the pointer cursor + drops the hover state. The
  // click handler stays attached but the consumer's `onTap` is a no-op
  // for those games.
  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'gv-endgame-inline-card';
  card.setAttribute('aria-label', 'View result');

  const banner = document.createElement('div');
  banner.className = 'gv-endgame-inline-banner';
  const bannerText = document.createElement('span');
  bannerText.className = 'gv-endgame-inline-banner-text';
  banner.appendChild(bannerText);
  card.appendChild(banner);

  const body = document.createElement('div');
  body.className = 'gv-endgame-inline-body';
  card.appendChild(body);

  const statHero = document.createElement('p');
  statHero.className = 'gv-endgame-inline-hero';
  statHero.hidden = true;
  body.appendChild(statHero);

  const statTime = document.createElement('p');
  statTime.className = 'gv-endgame-inline-time';
  statTime.hidden = true;
  body.appendChild(statTime);

  const statPrimary = document.createElement('p');
  statPrimary.className = 'gv-endgame-inline-primary';
  body.appendChild(statPrimary);

  const statLines = document.createElement('div');
  statLines.className = 'gv-endgame-inline-lines';
  body.appendChild(statLines);

  // Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game raw points row,
  // mirrored from the modal so the inline card carries the same "Score: N
  // pts" line above the rank chip after the player dismisses the modal.
  const pointsBlock = document.createElement('div');
  pointsBlock.className = 'gv-endgame-inline-points';
  pointsBlock.hidden = true;
  const pointsValue = document.createElement('p');
  pointsValue.className = 'gv-endgame-inline-points-value';
  pointsBlock.appendChild(pointsValue);
  body.appendChild(pointsBlock);

  const rankBlock = document.createElement('div');
  rankBlock.className = 'gv-endgame-inline-rank';
  rankBlock.hidden = true;
  const rankLabel = document.createElement('p');
  rankLabel.className = 'gv-endgame-inline-rank-label';
  const rankCaption = document.createElement('p');
  rankCaption.className = 'gv-endgame-inline-rank-caption';
  rankBlock.append(rankLabel, rankCaption);
  body.appendChild(rankBlock);

  // Tappable hint chip - the whole card is the target but a small
  // "Tap to view ..." hint helps discoverability. Defaults to
  // "Tap to view result"; spatial-config games override to
  // "Tap to view stats" (the board behind already shows the solution).
  const hint = document.createElement('p');
  hint.className = 'gv-endgame-inline-hint';
  hint.textContent = 'Tap to view result';
  body.appendChild(hint);

  return {
    card,
    banner,
    bannerText,
    body,
    statHero,
    statTime,
    statPrimary,
    statLines,
    pointsBlock,
    pointsValue,
    rankBlock,
    rankLabel,
    rankCaption,
    hint,
  };
}

export function mountEndGameInlineCard(
  target: HTMLElement,
  props: EndGameInlineCardProps,
): EndGameInlineCardHandle {
  const refs = buildSkeleton();
  let current = props;
  let destroyed = false;
  // Track rank-pill interactive listeners so we can detach when the next
  // render no longer has an href, or on teardown. The parent card is a
  // <button>; the rank handler MUST stopPropagation/preventDefault so the
  // hash navigation does not also re-open the modal via card.onTap.
  let rankClickHandler: ((event: Event) => void) | null = null;
  let rankKeydownHandler: ((event: KeyboardEvent) => void) | null = null;

  function syncStatLines(lines: string[]): void {
    refs.statLines.replaceChildren();
    for (const l of lines) {
      const p = document.createElement('p');
      p.className = 'gv-endgame-inline-line';
      p.textContent = l;
      refs.statLines.appendChild(p);
    }
  }

  function onCardClick(): void {
    if (destroyed) return;
    // PM 2026-06-11 (tap-to-view audit): when hintText is the empty
    // string the card is non-interactive (Connect4 et al). Suppress the
    // re-open call regardless of how the click was triggered (keyboard /
    // touch / synthetic) so there is no way to re-mount the modal.
    if (current.hintText === '') return;
    current.onTap();
  }
  refs.card.addEventListener('click', onCardClick);

  function render(p: EndGameInlineCardProps): void {
    current = p;
    const barOutcome = statBarOutcome(p.outcome);
    refs.card.setAttribute('data-outcome', barOutcome);
    refs.banner.setAttribute('data-outcome', barOutcome);
    refs.bannerText.textContent = p.bannerText ?? defaultBannerText(p.outcome);

    if (p.stats.hero) {
      refs.statHero.textContent = p.stats.hero;
      refs.statHero.setAttribute('data-outcome', barOutcome);
      refs.statHero.hidden = false;
    } else {
      refs.statHero.textContent = '';
      refs.statHero.removeAttribute('data-outcome');
      refs.statHero.hidden = true;
    }
    if (p.stats.time) {
      refs.statTime.textContent = p.stats.time;
      refs.statTime.setAttribute('data-outcome', barOutcome);
      refs.statTime.hidden = false;
    } else {
      refs.statTime.textContent = '';
      refs.statTime.hidden = true;
    }
    refs.statPrimary.textContent = p.stats.primary;
    syncStatLines(p.stats.lines);
    // Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game points row.
    // Mirrors the modal so the dismissed-state card still reads "Score: N
    // pts" above the rank chip.
    if (typeof p.stats.points === 'number' && Number.isFinite(p.stats.points) && p.stats.points >= 0) {
      refs.pointsValue.textContent = `Score: ${p.stats.points} pts`;
      refs.pointsBlock.hidden = false;
    } else {
      refs.pointsValue.textContent = '';
      refs.pointsBlock.hidden = true;
    }
    // PM 2026-06-19 (Bowz brief): par-aware scorecard colour mirror.
    applyParClassAttr(refs.pointsValue, p.stats.parContext);
    if (p.stats.rank) {
      refs.rankLabel.textContent = p.stats.rank.label;
      refs.rankCaption.textContent = p.stats.rank.caption;
      refs.rankBlock.hidden = false;
      // Clickable rank pill: navigate to the supplied hash route on click /
      // Enter / Space. The parent card is a <button> with its own onTap
      // (re-mounts the modal), so we MUST stopPropagation + preventDefault
      // here to avoid the modal re-opening on top of the navigation.
      const href = p.stats.rank.href;
      if (href) {
        refs.rankBlock.setAttribute('role', 'link');
        refs.rankBlock.setAttribute('tabindex', '0');
        refs.rankBlock.dataset.interactive = 'true';
        if (rankClickHandler) {
          refs.rankBlock.removeEventListener('click', rankClickHandler);
        }
        if (rankKeydownHandler) {
          refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
        }
        rankClickHandler = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          window.location.hash = href;
        };
        rankKeydownHandler = (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            event.stopPropagation();
            window.location.hash = href;
          }
        };
        refs.rankBlock.addEventListener('click', rankClickHandler);
        refs.rankBlock.addEventListener('keydown', rankKeydownHandler);
      } else {
        refs.rankBlock.removeAttribute('role');
        refs.rankBlock.removeAttribute('tabindex');
        delete refs.rankBlock.dataset.interactive;
        if (rankClickHandler) {
          refs.rankBlock.removeEventListener('click', rankClickHandler);
          rankClickHandler = null;
        }
        if (rankKeydownHandler) {
          refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
          rankKeydownHandler = null;
        }
      }
    } else {
      refs.rankBlock.hidden = true;
      refs.rankBlock.removeAttribute('role');
      refs.rankBlock.removeAttribute('tabindex');
      delete refs.rankBlock.dataset.interactive;
      if (rankClickHandler) {
        refs.rankBlock.removeEventListener('click', rankClickHandler);
        rankClickHandler = null;
      }
      if (rankKeydownHandler) {
        refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
        rankKeydownHandler = null;
      }
    }
    // PM 2026-06-11 (tap-to-view audit): empty hintText -> non-interactive
    // card (Connect4 et al). Hide the hint chip, drop the interactive
    // styling flag, remove the button's pointer affordance.
    if (p.hintText === '') {
      refs.hint.textContent = '';
      refs.hint.hidden = true;
      refs.card.setAttribute('data-interactive', 'false');
      refs.card.setAttribute('aria-label', 'Game result');
      refs.card.disabled = true;
    } else {
      refs.hint.textContent = p.hintText ?? 'Tap to view result';
      refs.hint.hidden = false;
      refs.card.setAttribute('data-interactive', 'true');
      refs.card.setAttribute('aria-label', 'View result');
      refs.card.disabled = false;
    }
  }

  render(props);
  target.appendChild(refs.card);

  return {
    update(next: EndGameInlineCardProps): void {
      render(next);
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      refs.card.removeEventListener('click', onCardClick);
      if (rankClickHandler) {
        refs.rankBlock.removeEventListener('click', rankClickHandler);
        rankClickHandler = null;
      }
      if (rankKeydownHandler) {
        refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
        rankKeydownHandler = null;
      }
      refs.card.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------
// PM 2026-06-11 (rotation-staleness bug): snapshots are keyed by
// `gives.endgame.<gameId>.<utcDate>.<puzzleNumber>`. The old format
// (`gives.endgame.<gameId>.<utcDate>`) silently rehydrated a stale snapshot
// from puzzle #N when test mode had rotated to puzzle #N+1 (UTC date
// unchanged across the 10-minute bucket flip). Including the puzzle number
// in the key makes that impossible.
//
// `loadEndgameSnapshot()` accepts an optional `expectedPuzzleNumber`:
//   - if provided (homepage tile / test-mode rehydrate): only the snapshot
//     for that exact puzzle is returned, otherwise null;
//   - if absent (production per-game rehydrate, where one puzzle per day):
//     the LATEST snapshot for the date is returned via the legacy date scan,
//     preserving back-compat.

export interface EndgameSnapshot {
  /** ISO `YYYY-MM-DD` for the UTC day the snapshot was taken. */
  utcDate: string;
  /** Game-internal puzzle number, mostly for sanity. */
  puzzleNumber: number;
  outcome: InlineCardOutcome;
  stats: EndGameStats;
  /** Optional banner text override, kept in sync with the modal. */
  bannerText?: string;
  /** Wall-clock ms when the snapshot was written. */
  savedAt: number;
  /**
   * Phase 19 (PM 2026-06-11): player's running cross-game point total today.
   * Forwarded straight from `/api/puzzle/<game>/finish` so the homepage tile
   * and end-state rank pill can show it without re-fetching.
   * `undefined` when the player is anonymous OR when the worker did not
   * return scoring fields (legacy snapshots / Wave 2E not yet shipped).
   */
  dailyTotal?: number;
  /**
   * Phase 19: 1-indexed daily rank. `null` for anonymous players or when the
   * worker omits scoring fields. `undefined` distinguishes "snapshot taken
   * before Phase 19 wiring" from "explicitly anonymous".
   */
  dailyRank?: number | null;
  /**
   * Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game raw score (the
   * worker's `gamePoints` value, 0..100ish) for THIS solve. Surfaced as
   * "Score: N pts" in the end-game modal + inline card. `undefined` when
   * the game doesn't run through the scoring engine (anagram-sprint) or
   * the snapshot pre-dates this field.
   *
   * The same value is also mirrored into `stats.points` so the components
   * can render it directly; this top-level mirror exists so consumers
   * (homepage tile, stats overlay) can read it without descending into
   * `stats`.
   */
  gamePoints?: number;
}

export function utcDateString(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * Storage key for a snapshot.
 *
 * When `puzzleNumber` is provided, returns the new format
 * (`gives.endgame.<gameId>.<utcDate>.<puzzleNumber>`).
 *
 * When `puzzleNumber` is omitted, returns the legacy format
 * (`gives.endgame.<gameId>.<utcDate>`). The legacy form is preserved so the
 * one-shot migration in `purgeLegacyEndgameKeys()` can identify and remove
 * pre-2026-06-11 keys. Production callers always pass a number.
 */
export function endgameStorageKey(
  gameId: string,
  utcDate: string,
  puzzleNumber?: number,
): string {
  if (puzzleNumber === undefined) {
    return `gives.endgame.${gameId}.${utcDate}`;
  }
  return `gives.endgame.${gameId}.${utcDate}.${puzzleNumber}`;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function saveEndgameSnapshot(gameId: string, snap: EndgameSnapshot): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(
      endgameStorageKey(gameId, snap.utcDate, snap.puzzleNumber),
      JSON.stringify(snap),
    );
  } catch {
    // ignore quota / serialisation errors
  }
}

function parseSnapshot(raw: string | null): EndgameSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<EndgameSnapshot> & {
      stats?: Partial<EndGameStats>;
    };
    if (
      parsed &&
      typeof parsed.utcDate === 'string' &&
      typeof parsed.puzzleNumber === 'number' &&
      (parsed.outcome === 'won' ||
        parsed.outcome === 'lost' ||
        parsed.outcome === 'timed_out') &&
      parsed.stats &&
      typeof parsed.stats.primary === 'string' &&
      Array.isArray(parsed.stats.lines) &&
      typeof parsed.savedAt === 'number'
    ) {
      return parsed as EndgameSnapshot;
    }
  } catch {
    // fall through
  }
  return null;
}

/**
 * Load a snapshot.
 *
 *   - `expectedPuzzleNumber` provided: returns only the snapshot for that
 *     exact puzzle (new key format). Returns null if no snapshot exists for
 *     that puzzle, or if the parsed payload's `puzzleNumber` does not match.
 *   - `expectedPuzzleNumber` omitted: scans every key under
 *     `gives.endgame.<gameId>.<utcDate>` (both legacy and new format) and
 *     returns the snapshot with the highest `savedAt`. This preserves the
 *     pre-2026-06-11 contract for per-game rehydrate call sites in
 *     production (one puzzle per day - the latest IS the only one).
 */
export function loadEndgameSnapshot(
  gameId: string,
  utcDate: string,
  expectedPuzzleNumber?: number,
): EndgameSnapshot | null {
  const s = safeStorage();
  if (!s) return null;
  if (typeof expectedPuzzleNumber === 'number') {
    const snap = parseSnapshot(
      s.getItem(endgameStorageKey(gameId, utcDate, expectedPuzzleNumber)),
    );
    if (!snap) return null;
    if (snap.puzzleNumber !== expectedPuzzleNumber) return null;
    return snap;
  }
  // No expected number -> find latest snapshot for the date across both
  // legacy (`...<utcDate>`) and new (`...<utcDate>.<puzzleNumber>`) keys.
  const prefix = `gives.endgame.${gameId}.${utcDate}`;
  let best: EndgameSnapshot | null = null;
  try {
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (!key) continue;
      if (key !== prefix && !key.startsWith(`${prefix}.`)) continue;
      const snap = parseSnapshot(s.getItem(key));
      if (!snap) continue;
      if (snap.utcDate !== utcDate) continue;
      if (!best || snap.savedAt > best.savedAt) best = snap;
    }
  } catch {
    // ignore - storage may be unavailable
  }
  return best;
}

export function clearEndgameSnapshot(
  gameId: string,
  utcDate: string,
  puzzleNumber?: number,
): void {
  const s = safeStorage();
  if (!s) return;
  try {
    if (typeof puzzleNumber === 'number') {
      s.removeItem(endgameStorageKey(gameId, utcDate, puzzleNumber));
      return;
    }
    // Remove every snapshot under the prefix (legacy + every puzzle today).
    const prefix = `gives.endgame.${gameId}.${utcDate}`;
    const toDelete: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (!key) continue;
      if (key === prefix || key.startsWith(`${prefix}.`)) toDelete.push(key);
    }
    for (const k of toDelete) s.removeItem(k);
  } catch {
    // ignore
  }
}

/**
 * One-shot migration: purge legacy snapshot keys (no trailing puzzle number)
 * left over from before the 2026-06-11 fix. The schema change is otherwise
 * transparent (new keys coexist), but legacy keys would otherwise stay in
 * localStorage forever since nothing reads them in the new code paths
 * outside the back-compat scan above. Call once from module init.
 */
export function purgeLegacyEndgameKeys(): void {
  const s = safeStorage();
  if (!s) return;
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (!key) continue;
      if (!key.startsWith('gives.endgame.')) continue;
      // Strip the `gives.endgame.` prefix and split on `.`. A legacy key
      // looks like `gives.endgame.<gameId>.<YYYY-MM-DD>` - 4 tokens by `.`.
      // A new key has a trailing numeric puzzle number, giving 5 tokens
      // (the date itself contains no dots because it's `YYYY-MM-DD`).
      const tail = key.slice('gives.endgame.'.length);
      const parts = tail.split('.');
      if (parts.length === 2) {
        // 2 parts after the prefix = legacy (`<gameId>.<utcDate>`).
        toDelete.push(key);
      }
    }
    for (const k of toDelete) s.removeItem(k);
  } catch {
    // ignore
  }
}
